import { FilterOperator } from "domain/asset-management/assetFilterConstants";
import { AssetPathKey } from "domain/asset-management/assetPathMapping";
import { GRADING_ACTION_DETAIL_KEY } from "domain/asset-management/assets";
import { Asset, AssetFilter, AssetSearchRequest } from "services/asset-management/AssetService";

export const GRADING_TABLE_PAGE_SIZE = 50;

export enum GradeTab {
    ALL = "ALL",
    GRADE_A = "A",
    GRADE_B = "B",
    GRADE_C = "C",
    GRADE_D = "D",
    NO_GRADE = "NO_GRADE",
}

const GRADING_TABLE_PATHS: string[] = [
    AssetPathKey.ASSET_ID,
    AssetPathKey.UUID,
    AssetPathKey.ASSET_TYPE,
    AssetPathKey.MODEL,
    AssetPathKey.BLANCCO_GRADE,
    GRADING_ACTION_DETAIL_KEY,
    AssetPathKey.ASSET_VALUE,
    AssetPathKey.POTENTIAL_UPLIFT_VALUE,
];

const GRADING_EXPORT_PATHS: string[] = [
    AssetPathKey.ASSET_ID,
    AssetPathKey.ASSET_TYPE,
    AssetPathKey.MODEL,
    AssetPathKey.BLANCCO_GRADE,
    GRADING_ACTION_DETAIL_KEY,
    AssetPathKey.ASSET_VALUE,
    AssetPathKey.POTENTIAL_UPLIFT_VALUE,
    AssetPathKey.TOTAL_VALUE,
    AssetPathKey.OPTIMIZATION_SUGGESTIONS,
];

function toGradeFilter(activeTab: GradeTab): AssetFilter | undefined {
    if (activeTab === GradeTab.ALL) {
        return undefined;
    }
    if (activeTab === GradeTab.NO_GRADE) {
        return { name: AssetPathKey.BLANCCO_GRADE, value: "", operator: FilterOperator.NOT_EXIST };
    }
    return { name: AssetPathKey.BLANCCO_GRADE, value: activeTab, operator: FilterOperator.EQUAL };
}

function toDeviceTypeFilter(selectedDeviceType?: string): AssetFilter | undefined {
    if (selectedDeviceType === undefined) {
        return undefined;
    }
    return { name: AssetPathKey.ASSET_TYPE, value: selectedDeviceType.toLowerCase(), operator: FilterOperator.MATCH };
}

export function buildGradingTableQuickFilters(activeTab: GradeTab, selectedDeviceType?: string): AssetFilter[] {
    return [toGradeFilter(activeTab), toDeviceTypeFilter(selectedDeviceType)].filter(
        (filter): filter is AssetFilter => filter !== undefined
    );
}

export function buildGradingTableSearchRequest(
    baseRequest: AssetSearchRequest | undefined,
    quickFilters: AssetFilter[]
): AssetSearchRequest {
    const request: AssetSearchRequest = { ...(baseRequest ?? { paths: [] }), paths: GRADING_TABLE_PATHS };
    if (quickFilters.length === 0) {
        return request;
    }
    return { ...request, quickFilters: [...(baseRequest?.quickFilters ?? []), ...quickFilters] };
}

export function buildGradingTablePageRequest(
    baseRequest: AssetSearchRequest | undefined,
    quickFilters: AssetFilter[],
    pageNumber: number
): AssetSearchRequest {
    return {
        ...buildGradingTableSearchRequest(baseRequest, quickFilters),
        size: GRADING_TABLE_PAGE_SIZE,
        cursor: String(pageNumber - 1),
    };
}

export function buildGradingExportRequest(
    baseRequest: AssetSearchRequest | undefined,
    quickFilters: AssetFilter[]
): AssetSearchRequest {
    return { ...buildGradingTableSearchRequest(baseRequest, quickFilters), paths: GRADING_EXPORT_PATHS };
}

export function replaceAssetValues(assets: Asset[], updated: Asset): Asset[] {
    return assets.map((asset) =>
        asset.uuid === updated.uuid
            ? {
                  ...asset,
                  [AssetPathKey.ASSET_VALUE]: updated[AssetPathKey.ASSET_VALUE],
                  [AssetPathKey.POTENTIAL_UPLIFT_VALUE]: updated[AssetPathKey.POTENTIAL_UPLIFT_VALUE],
              }
            : asset
    );
}

export function addMissingUuids(uuids: string[], addedUuids: string[]): string[] {
    return [...uuids, ...addedUuids.filter((uuid) => !uuids.includes(uuid))];
}

export function formatDeviceTypeLabel(deviceType: string): string {
    return deviceType.charAt(0).toUpperCase() + deviceType.slice(1);
}
